import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Monitor, Search, Star, ExternalLink, ChevronLeft, ChevronRight, 
  Ship, Truck, Package, BarChart3, MapPin, FileText, Clock, 
  CheckCircle, Globe, Play, Image, ArrowRight, Plus, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toFullUrl } from '../utils/imageUrl';

const SoftwareMarketplace = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [software, setSoftware] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedSoftware, setSelectedSoftware] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchSoftware();
  }, []);

  const fetchSoftware = async () => {
    try {
      const response = await api.get('/software');
      setSoftware(response.data);
    } catch (error) {
      // Use sample data if API not available
      setSoftware([]);
    } finally {
      setLoading(false);
    }
  };

  // Sample software for demonstration
  const sampleSoftware = [
    {
      id: '1',
      name: 'CargoTrack Pro',
      company_name: 'Caribbean Tech Solutions',
      category: 'tms',
      tagline: 'Complete Transportation Management System for Caribbean Freight',
      description: 'CargoTrack Pro is a comprehensive TMS designed specifically for Caribbean freight operations. Manage bookings, track shipments in real-time, automate documentation, and streamline your entire logistics workflow.',
      features: ['Real-time tracking', 'Automated documentation', 'Multi-modal support', 'Customer portal', 'API integrations', 'Mobile app'],
      screenshots: [
        'https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/5716001/pexels-photo-5716001.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/7688460/pexels-photo-7688460.jpeg?auto=compress&cs=tinysrgb&w=800',
      ],
      logo_url: 'https://images.pexels.com/photos/5716001/pexels-photo-5716001.jpeg?auto=compress&cs=tinysrgb&w=200',
      website: 'https://cargotrackpro.com',
      pricing: 'Starting at $299/month',
      rating: 4.8,
      reviews_count: 47,
      verified: true
    },
    {
      id: '2',
      name: 'IslandWMS',
      company_name: 'Logistics Software Inc.',
      category: 'wms',
      tagline: 'Warehouse Management Built for Island Operations',
      description: 'IslandWMS helps Caribbean warehouses optimize storage, manage inventory, and coordinate with freight partners. Perfect for bonded warehouses and free zone operations.',
      features: ['Inventory management', 'Barcode scanning', 'Pick and pack', 'Customs integration', 'Multi-warehouse', 'Reporting'],
      screenshots: [
        'https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg?auto=compress&cs=tinysrgb&w=800',
      ],
      logo_url: 'https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg?auto=compress&cs=tinysrgb&w=200',
      website: 'https://islandwms.com',
      pricing: 'From $199/month',
      rating: 4.5,
      reviews_count: 32,
      verified: true
    },
    {
      id: '3',
      name: 'FreightQuote Express',
      company_name: 'QuoteTech Caribbean',
      category: 'quoting',
      tagline: 'Instant Freight Quotes Across All Modes',
      description: 'Generate accurate freight quotes in seconds. Integrate with your TMS or use standalone. Supports air, ocean, and land freight with real-time carrier rates.',
      features: ['Instant quotes', 'Rate management', 'Carrier integration', 'Margin calculator', 'Customer self-service', 'Analytics'],
      screenshots: [
        'https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?auto=compress&cs=tinysrgb&w=800',
      ],
      logo_url: 'https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?auto=compress&cs=tinysrgb&w=200',
      website: 'https://freightquoteexpress.com',
      pricing: '$149/month',
      rating: 4.6,
      reviews_count: 28,
      verified: true
    },
    {
      id: '4',
      name: 'CustomsClear',
      company_name: 'BorderTech Solutions',
      category: 'customs',
      tagline: 'Simplify Caribbean Customs Clearance',
      description: 'Automate customs declarations, manage duties and taxes, and ensure compliance across all Caribbean territories. Direct integration with customs authorities.',
      features: ['Auto-classification', 'HS code lookup', 'Duty calculator', 'Document generation', 'Compliance alerts', 'Multi-territory'],
      screenshots: [
        'https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/4386442/pexels-photo-4386442.jpeg?auto=compress&cs=tinysrgb&w=800',
      ],
      logo_url: 'https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&w=200',
      website: 'https://customsclear.com',
      pricing: 'Custom pricing',
      rating: 4.7,
      reviews_count: 19,
      verified: false
    },
    {
      id: '5',
      name: 'ShipTracker Mobile',
      company_name: 'MobileFreight Apps',
      category: 'tracking',
      tagline: 'Real-Time Shipment Visibility on Any Device',
      description: 'Give your customers real-time visibility into their shipments. White-label tracking portal with SMS and email notifications. Works with any TMS.',
      features: ['GPS tracking', 'Customer portal', 'SMS alerts', 'Proof of delivery', 'ETA predictions', 'White-label'],
      screenshots: [
        'https://images.pexels.com/photos/4386476/pexels-photo-4386476.jpeg?auto=compress&cs=tinysrgb&w=800',
      ],
      logo_url: 'https://images.pexels.com/photos/4386476/pexels-photo-4386476.jpeg?auto=compress&cs=tinysrgb&w=200',
      website: 'https://shiptrackermobile.com',
      pricing: '$99/month',
      rating: 4.4,
      reviews_count: 56,
      verified: true
    }
  ];

  const displaySoftware = software.length > 0 ? software : sampleSoftware;

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'tms', label: 'Transportation Management (TMS)' },
    { value: 'wms', label: 'Warehouse Management (WMS)' },
    { value: 'quoting', label: 'Quoting & Rating' },
    { value: 'customs', label: 'Customs & Compliance' },
    { value: 'tracking', label: 'Tracking & Visibility' },
    { value: 'accounting', label: 'Accounting & Billing' },
  ];

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'tms': return <Truck className="h-4 w-4" />;
      case 'wms': return <Package className="h-4 w-4" />;
      case 'quoting': return <FileText className="h-4 w-4" />;
      case 'customs': return <Globe className="h-4 w-4" />;
      case 'tracking': return <MapPin className="h-4 w-4" />;
      case 'accounting': return <BarChart3 className="h-4 w-4" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const filteredSoftware = displaySoftware.filter(sw => {
    const matchesSearch = sw.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sw.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sw.tagline.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || sw.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`}
          />
        ))}
        <span className="ml-1 text-sm font-medium">{rating}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <>
        {user ? <Header /> : null}
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  return (
    <>
      {user ? <Header /> : (
        <nav className="fixed top-4 left-1/2 -translate-x-1/2 w-[90%] max-w-7xl bg-white/90 backdrop-blur-md border border-slate-200/50 rounded-full shadow-lg z-50 px-6 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Ship className="h-8 w-8 text-cyan-500" />
              <span className="text-xl font-outfit font-bold text-ocean-900">
                <span className="text-cyan-500">CTC</span> Cargo Network
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/login')}>Sign In</Button>
              <Button onClick={() => navigate('/register')} className="bg-cyan-500 hover:bg-cyan-600">Join Network</Button>
            </div>
          </div>
        </nav>
      )}

      <div className={`min-h-screen bg-slate-50 ${user ? '' : 'pt-24'}`}>
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <Badge className="bg-white/20 text-white mb-4">Software Marketplace</Badge>
              <h1 className="text-4xl md:text-5xl font-outfit font-bold mb-4">
                Discover Cargo Software Solutions
              </h1>
              <p className="text-xl text-indigo-200 max-w-2xl mx-auto mb-8">
                Find the perfect TMS, WMS, tracking, and customs software for your Caribbean freight operations
              </p>
              
              {user && (
                <Button 
                  onClick={() => navigate('/software/submit')}
                  className="bg-white text-indigo-900 hover:bg-indigo-100"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  List Your Software
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <Card className="shadow-lg">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    placeholder="Search software by name, company, or feature..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-11 py-6 text-lg"
                    data-testid="software-search"
                  />
                </div>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-full md:w-64 py-6">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <span className="flex items-center gap-2">
                          {getCategoryIcon(cat.value)}
                          {cat.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Software Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-sm text-slate-600 mb-6">
            Showing {filteredSoftware.length} software solutions
          </p>

          {filteredSoftware.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Monitor className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No software found</h3>
                <p className="text-slate-600 mb-4">Try adjusting your search or filters</p>
                {user && (
                  <Button onClick={() => navigate('/software/submit')} className="bg-cyan-500 hover:bg-cyan-600">
                    Be the first to list software in this category
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSoftware.map((sw) => (
                <motion.div
                  key={sw.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card 
                    className="h-full hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
                    onClick={() => setSelectedSoftware(sw)}
                    data-testid={`software-card-${sw.id}`}
                  >
                    {/* Screenshot Preview */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={sw.screenshots[0]} 
                        alt={sw.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-3 left-3 right-3 flex justify-between items-end">
                        <Badge className="bg-white/90 text-slate-700">
                          {getCategoryIcon(sw.category)}
                          <span className="ml-1">{getCategoryLabel(sw.category).split(' ')[0]}</span>
                        </Badge>
                        {sw.verified && (
                          <Badge className="bg-green-500 text-white">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                      </div>
                    </div>

                    <CardContent className="p-5">
                      <div className="flex items-start gap-3 mb-3">
                        <img 
                          src={toFullUrl(sw.logo_url)} 
                          alt={sw.company_name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-lg text-ocean-900 truncate">{sw.name}</h3>
                          <p className="text-sm text-slate-500 truncate">{sw.company_name}</p>
                        </div>
                      </div>

                      <p className="text-slate-600 text-sm mb-4 line-clamp-2">{sw.tagline}</p>

                      <div className="flex items-center justify-between mb-4">
                        {renderStars(sw.rating)}
                        <span className="text-xs text-slate-500">({sw.reviews_count} reviews)</span>
                      </div>

                      <div className="flex flex-wrap gap-1 mb-4">
                        {sw.features.slice(0, 3).map((feature, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {feature}
                          </Badge>
                        ))}
                        {sw.features.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{sw.features.length - 3}
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t">
                        <span className="text-sm font-medium text-green-600">{sw.pricing}</span>
                        <Button size="sm" variant="ghost" className="text-cyan-600 hover:text-cyan-700">
                          View Details <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}

          {software.length === 0 && (
            <div className="text-center mt-8 text-slate-500">
              <p className="text-sm">These are sample listings. Software providers can list their solutions once approved.</p>
            </div>
          )}
        </div>

        {/* Software Detail Modal */}
        <Dialog open={!!selectedSoftware} onOpenChange={() => setSelectedSoftware(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {selectedSoftware && (
              <>
                <DialogHeader>
                  <div className="flex items-start gap-4">
                    <img 
                      src={toFullUrl(selectedSoftware.logo_url)} 
                      alt={selectedSoftware.company_name}
                      className="w-16 h-16 rounded-xl object-cover"
                    />
                    <div>
                      <DialogTitle className="text-2xl">{selectedSoftware.name}</DialogTitle>
                      <p className="text-slate-600">{selectedSoftware.company_name}</p>
                      <div className="flex items-center gap-3 mt-2">
                        {renderStars(selectedSoftware.rating)}
                        <span className="text-sm text-slate-500">({selectedSoftware.reviews_count} reviews)</span>
                        {selectedSoftware.verified && (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Verified Vendor
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </DialogHeader>

                {/* Screenshot Gallery */}
                <div className="mt-6">
                  <div className="relative rounded-xl overflow-hidden bg-slate-100">
                    <img 
                      src={selectedSoftware.screenshots[currentImageIndex]} 
                      alt={`Screenshot ${currentImageIndex + 1}`}
                      className="w-full h-64 md:h-96 object-cover"
                    />
                    {selectedSoftware.screenshots.length > 1 && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImageIndex(prev => prev === 0 ? selectedSoftware.screenshots.length - 1 : prev - 1);
                          }}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImageIndex(prev => prev === selectedSoftware.screenshots.length - 1 ? 0 : prev + 1);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                          {selectedSoftware.screenshots.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentImageIndex(idx);
                              }}
                              className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-white' : 'bg-white/50'}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Thumbnail strip */}
                  {selectedSoftware.screenshots.length > 1 && (
                    <div className="flex gap-2 mt-3">
                      {selectedSoftware.screenshots.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`w-20 h-14 rounded-lg overflow-hidden border-2 transition-colors ${idx === currentImageIndex ? 'border-cyan-500' : 'border-transparent'}`}
                        >
                          <img src={img} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="mt-6">
                  <h4 className="font-semibold text-lg mb-2">About</h4>
                  <p className="text-slate-600">{selectedSoftware.description}</p>
                </div>

                {/* Features */}
                <div className="mt-6">
                  <h4 className="font-semibold text-lg mb-3">Key Features</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedSoftware.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pricing and CTA */}
                <div className="mt-6 pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Pricing</p>
                    <p className="text-2xl font-bold text-green-600">{selectedSoftware.pricing}</p>
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => window.open(selectedSoftware.website, '_blank')}>
                      <Globe className="mr-2 h-4 w-4" />
                      Visit Website
                    </Button>
                    <Button className="bg-cyan-500 hover:bg-cyan-600">
                      Request Demo
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default SoftwareMarketplace;
